import React, { useState } from "react";
import { Banner, Button, Radio, RadioGroup } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import { LANGUAGE_MODAL_NAMESPACE } from "constants/i18n-namespaces";
import { ENGLISH, FRENCH, PLATFORM_NAMESPACE, SPANISH } from "kubra-labels";
import useLabels from "hooks/useLabels";
import { useLabelsWithKrn } from "hooks";
import useBiller from "hooks/useBiller";
import { getDomain, mapUiLanguageToSessionLanguage } from "utilities/helper";
import { getLanguageLabel, getSupportedLanguageCodes } from "utilities/language";

export interface LanguageModalProps {
  onClose?: () => void;
}

export const LanguageModal = ({ onClose }: LanguageModalProps) => {
  const { t, i18n } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");

  const [selectedLanguage, setSelectedLanguage] = useState(
    i18n.language || ENGLISH
  );
  const { getBiller } = useBiller();
  const biller = getBiller();
  const { loadLabels } = useLabels();
  const { loadLabelsWithKrn } = useLabelsWithKrn()

  const supportedLanguageCodes = getSupportedLanguageCodes(biller);

  const updateLanguage = () => {
    setErrorMessage("");
    i18n
      .changeLanguage(selectedLanguage)
      .then(() => {
        updateLabelsForLanguage();
        if (onClose) {
          onClose();
        }
      })
      .catch(() => {
        const errorMsg = t("error-changing-language", {
          ns: LANGUAGE_MODAL_NAMESPACE,
        });
        setErrorMessage(errorMsg);
      });
  };

  // Load labels
  const updateLabelsForLanguage = () => {
    if (!(biller?.clientId && biller?.realm && biller?.instanceId)) return;
    const { clientId, realm, instanceId } = biller;
    const language = mapUiLanguageToSessionLanguage(i18n.resolvedLanguage ?? "en");
    const domain = getDomain();
    loadLabels(clientId, instanceId, domain, realm, language, biller.payableTypeDefinitions ?? [], biller.featureFlags?.payableUi ?? "false");
    loadLabelsWithKrn(clientId, instanceId, domain, realm, language, biller.payableTypeDefinitions ?? [], biller.featureFlags?.payableUi ?? "false");
  };

  return (
    <div className="language-modal">
      {errorMessage && <Banner messageLabel={errorMessage} data-cy="banner-error-content" variant="error" withIcon/>}
      <RadioGroup
        groupName="language"
        label={`${t("language-radio-group-label", {
          ns: LANGUAGE_MODAL_NAMESPACE,
        })}`}
        onChange={(e) => setSelectedLanguage(e.currentTarget.value)}
        defaultValue={selectedLanguage}
        /* @ts-ignore */
        dataCy="radio-group-language"
      >
        {supportedLanguageCodes.map((languageCode) => (
          <Radio
            key={languageCode}
            label={getLanguageLabel(languageCode, "modal", t)}
            value={languageCode}
            dataCy={`radio-${languageCode.toLowerCase()}`}
          />
        ))}
      </RadioGroup>
      <Button
        onClick={updateLanguage}
        className="language-modal-save-button"
        dataCy="button-save"
      >
        {t("saveLabel", { ns: PLATFORM_NAMESPACE })}
      </Button>
    </div>
  );
};
